import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getDisplayInvoiceAmount, getInvoiceDueCountdownBadgeInfo } from '../utils/invoiceCardUtils';
import {
  dashboardBtnInvoiceEdit,
  dashboardBtnInvoiceActions,
  dashboardBtnInvoiceDelete,
} from '../components/dashboard/DashboardChrome';
import { devClickDebug } from '../devClickDebug';

const QuoteRow = ({
  quote,
  client,
  quoteTotal,
  editQuote,
  deleteQuote,
  handleSendEmail,
  canSendFromDevello = false,
  dropdownOpenId,
  setDropdownOpenId,
  forceDropdownScrollable,
  onCreateInvoice,
  onEditInvoice,
  onInvoiceSentStatusClick,
  onInvoiceAmountUpdate,
  onToggleInvoicePayment,
  quoteRowMutationBusyId = null,
}) => {
  const dropdownRef = useRef(null);
  const actionsBtnRef = useRef(null);
  const floatingMenuRef = useRef(null);
  const rowRef = useRef(null);
  const [floatingMenuStyle, setFloatingMenuStyle] = useState(null);
  const [amountEditOpen, setAmountEditOpen] = useState(false);
  const [amountDraft, setAmountDraft] = useState('');
  const amountInputRef = useRef(null);

  const invoiceAmountDisplay = getDisplayInvoiceAmount(quote);
  const dueCountdownBadgeInfo = getInvoiceDueCountdownBadgeInfo(quote);

  const rowMutationBusy = quoteRowMutationBusyId === quote.id;
  const canClickInvoiceSentBadge =
    !rowMutationBusy &&
    onInvoiceSentStatusClick &&
    (quote.status === 'draft' || quote.status === 'sent_invoice' || quote.status === 'sent_both');
  const invoiceSentBadgeTitle =
    quote.status === 'draft'
      ? 'Click to mark invoice as sent (e.g. sent outside Devello)'
      : quote.status === 'sent_invoice'
        ? 'Click to mark invoice as not sent (back to Draft)'
        : quote.status === 'sent_both'
          ? 'Click to unmark invoice sent (quote stays Sent Quote only)'
          : undefined;

  useEffect(() => {
    if (amountEditOpen && amountInputRef.current) {
      amountInputRef.current.focus();
      amountInputRef.current.select();
    }
  }, [amountEditOpen]);

  useEffect(() => {
    if (dropdownOpenId !== quote.id) return;
    function handleClickOutside(event) {
      const t = event.target;
      if (
        dropdownRef.current?.contains(t) ||
        floatingMenuRef.current?.contains(t)
      ) {
        return;
      }
      setDropdownOpenId(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpenId, quote.id, setDropdownOpenId]);

  const MENU_WIDTH = 208;

  const updateFloatingMenuPosition = useCallback(() => {
    if (dropdownOpenId !== quote.id) return;
    const btn = actionsBtnRef.current;
    if (!btn) return;
    const br = btn.getBoundingClientRect();
    const pad = 8;
    const menuEl = floatingMenuRef.current;
    const measured =
      menuEl && menuEl.offsetHeight > 0 ? menuEl.offsetHeight : 140;
    const scrollModeCap = forceDropdownScrollable
      ? Math.min(260, window.innerHeight - pad * 2)
      : Infinity;
    const naturalCap = Math.min(measured, scrollModeCap);
    let top = br.bottom + pad;
    let left = br.right - MENU_WIDTH;
    left = Math.max(pad, Math.min(left, window.innerWidth - MENU_WIDTH - pad));
    if (top + naturalCap > window.innerHeight - pad) {
      top = br.top - naturalCap - pad;
    }
    if (top < pad) top = pad;
    const maxFromViewport = Math.max(96, window.innerHeight - top - pad);
    const maxHeight = Math.min(naturalCap, maxFromViewport);

    setFloatingMenuStyle({
      top,
      left,
      width: MENU_WIDTH,
      maxHeight,
    });
  }, [dropdownOpenId, quote.id, forceDropdownScrollable]);

  useLayoutEffect(() => {
    if (dropdownOpenId !== quote.id) {
      setFloatingMenuStyle(null);
      return;
    }
    const tick = () => updateFloatingMenuPosition();
    tick();
    const raf = requestAnimationFrame(tick);
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(tick) : null;
    const node = floatingMenuRef.current;
    if (node && ro) ro.observe(node);
    window.addEventListener('scroll', tick, true);
    window.addEventListener('resize', tick);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener('scroll', tick, true);
      window.removeEventListener('resize', tick);
    };
  }, [dropdownOpenId, quote.id, updateFloatingMenuPosition]);

  const openAmountEditor = () => {
    if (!quote.invoiceData || !onInvoiceAmountUpdate) return;
    const v = getDisplayInvoiceAmount(quote);
    setAmountDraft(v != null ? String(v) : '');
    setAmountEditOpen(true);
  };

  const commitAmountEdit = () => {
    if (!onInvoiceAmountUpdate) return;
    const trimmed = amountDraft.trim().replace(/,/g, '');
    if (trimmed === '') {
      onInvoiceAmountUpdate(quote.id, null);
      setAmountEditOpen(false);
      return;
    }
    const n = parseFloat(trimmed);
    if (Number.isNaN(n) || n < 0) {
      alert('Enter a valid amount, or leave empty to use the invoice PDF total.');
      return;
    }
    onInvoiceAmountUpdate(quote.id, n);
    setAmountEditOpen(false);
  };

  const statusBadge = (
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${
        quote.status === 'draft'
          ? 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'
          : quote.status === 'sent_quote' || quote.status === 'sent_invoice' || quote.status === 'sent_both'
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-950/55 dark:text-blue-200'
            : quote.status === 'accepted'
              ? 'bg-green-100 text-green-700 dark:bg-emerald-900/65 dark:text-emerald-200'
              : quote.status === 'rejected'
                ? 'bg-red-100 text-red-700 dark:bg-red-950/55 dark:text-red-200'
                : quote.status === 'request_changes'
                  ? 'bg-yellow-100 text-yellow-700 dark:bg-amber-950/45 dark:text-amber-200'
                  : 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300'
      } ${canClickInvoiceSentBadge ? 'cursor-pointer hover:opacity-90 hover:ring-2 hover:ring-blue-300' : ''} ${
        rowMutationBusy ? 'opacity-60 pointer-events-none' : ''
      }`}
      onClick={
        canClickInvoiceSentBadge
          ? (e) => {
              e.stopPropagation();
              devClickDebug('QuoteRow: invoice/quote status badge click', {
                quoteId: quote.id,
                status: quote.status,
              });
              onInvoiceSentStatusClick(quote);
            }
          : undefined
      }
      title={rowMutationBusy ? 'Updating…' : invoiceSentBadgeTitle}
      role={canClickInvoiceSentBadge ? 'button' : undefined}
    >
      {quote.status === 'request_changes'
        ? 'Request Changes'
        : quote.status === 'sent_quote'
          ? 'Sent Quote'
          : quote.status === 'sent_invoice'
            ? 'Sent Invoice'
            : quote.status === 'sent_both'
              ? 'Sent Quote & Invoice'
              : quote.status}
    </span>
  );

  return (
    <div
      ref={rowRef}
      className="invoice-row-card rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm transition-all hover:border-slate-300 hover:shadow-md dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-start">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h4 className="text-lg font-semibold text-gray-900">
              {quote.quoteNumber ? quote.quoteNumber : `Invoice #${quote.id.slice(-6)}`}
            </h4>
            {statusBadge}

            {quote.invoiceData && (
              <>
                {amountEditOpen ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs">
                    <span className="font-medium text-teal-900">Invoice:</span>
                    <span className="text-teal-800">$</span>
                    <input
                      ref={amountInputRef}
                      type="text"
                      inputMode="decimal"
                      value={amountDraft}
                      onChange={(e) => setAmountDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitAmountEdit();
                        if (e.key === 'Escape') setAmountEditOpen(false);
                      }}
                      className="w-24 rounded border border-teal-300 bg-white px-1 py-0.5 text-xs text-teal-900"
                    />
                    <button
                      type="button"
                      className="text-xs font-semibold text-teal-800 hover:underline"
                      onClick={commitAmountEdit}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="text-xs text-gray-500 hover:underline"
                      onClick={() => setAmountEditOpen(false)}
                    >
                      Cancel
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={openAmountEditor}
                    className="rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-900 transition hover:bg-teal-100 dark:border-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-200 dark:hover:bg-emerald-900/65"
                    title="Click to edit amount (defaults to invoice PDF total)"
                  >
                    Invoice: $
                    {invoiceAmountDisplay != null
                      ? invoiceAmountDisplay.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })
                      : '—'}
                  </button>
                )}

                {dueCountdownBadgeInfo && onToggleInvoicePayment && (
                  <button
                    type="button"
                    disabled={rowMutationBusy}
                    onClick={() => onToggleInvoicePayment(quote)}
                    className={`rounded-full px-2 py-0.5 text-xs font-medium transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 ${
                      dueCountdownBadgeInfo.variant === 'paid'
                        ? 'invoice-paid-badge bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-900/70 dark:text-emerald-200 dark:ring-emerald-700'
                        : dueCountdownBadgeInfo.variant === 'overdue'
                          ? 'bg-red-100 text-red-900 ring-1 ring-red-200 dark:bg-red-950/60 dark:text-red-200 dark:ring-red-800'
                          : dueCountdownBadgeInfo.variant === 'soon'
                            ? 'bg-orange-100 text-orange-900 ring-1 ring-orange-200 dark:bg-orange-950/50 dark:text-orange-200 dark:ring-orange-800'
                            : dueCountdownBadgeInfo.variant === 'upcoming'
                              ? 'bg-slate-100 text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-600'
                              : 'bg-amber-50 text-amber-900 ring-1 ring-amber-200 dark:bg-amber-950/45 dark:text-amber-200 dark:ring-amber-800'
                    }`}
                    title={
                      dueCountdownBadgeInfo.variant === 'paid'
                        ? 'Click to mark as awaiting payment (show due date again)'
                        : 'Click to mark as paid'
                    }
                  >
                    {dueCountdownBadgeInfo.label}
                  </button>
                )}
              </>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
            <div>
              <span className="font-medium">Client:</span> {client?.name || 'Unknown'}
            </div>
            <div>
              <span className="font-medium">Total:</span> $
              {quoteTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div>
              <span className="font-medium">Edited On:</span>{' '}
              {quote.editedOn ? new Date(quote.editedOn).toLocaleString() : 'N/A'}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2 sm:flex-col sm:items-end">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => editQuote(quote)} className={`${dashboardBtnInvoiceEdit} shrink-0`}>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              Edit
            </button>
            <div className="relative shrink-0" ref={dropdownRef}>
              <button
                ref={actionsBtnRef}
                type="button"
                className={`${dashboardBtnInvoiceActions} flex items-center gap-1`}
                onClick={(e) => {
                  e.stopPropagation();
                  setDropdownOpenId(dropdownOpenId === quote.id ? null : quote.id);
                }}
              >
                Actions
                <svg className="ml-0.5 h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {dropdownOpenId === quote.id &&
                createPortal(
                  <div
                    ref={floatingMenuRef}
                    role="menu"
                    className="invoice-actions-menu fixed z-[10050] overflow-x-hidden overflow-y-auto rounded-xl border border-slate-200/90 bg-white py-1 shadow-xl shadow-slate-900/10 dark:border-slate-700 dark:bg-slate-900"
                    style={
                      floatingMenuStyle
                        ? {
                            top: floatingMenuStyle.top,
                            left: floatingMenuStyle.left,
                            width: floatingMenuStyle.width,
                            maxHeight: floatingMenuStyle.maxHeight,
                          }
                        : {
                            top: 0,
                            left: 0,
                            width: MENU_WIDTH,
                            maxHeight: 280,
                            visibility: 'hidden',
                            pointerEvents: 'none',
                          }
                    }
                  >
                    <ul className="text-sm text-slate-700">
                      <li>
                        <button
                          type="button"
                          className="w-full px-4 py-2.5 text-left font-medium transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                          onClick={() => {
                            if (quote.invoiceData && typeof onEditInvoice === 'function') {
                              onEditInvoice();
                            } else if (typeof onCreateInvoice === 'function') {
                              onCreateInvoice();
                            }
                            setDropdownOpenId(null);
                          }}
                        >
                          Invoice
                        </button>
                      </li>
                      {(quote.pdfData || quote.pdfDataReady || quote.invoiceData) && canSendFromDevello && (
                        <li>
                          <button
                            type="button"
                            className="w-full px-4 py-2.5 text-left transition-colors hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-800"
                            onClick={() => {
                              handleSendEmail(quote);
                              setDropdownOpenId(null);
                            }}
                          >
                            Send
                          </button>
                        </li>
                      )}
                    </ul>
                  </div>,
                  document.body
                )}
            </div>
            <button type="button" onClick={() => deleteQuote(quote.id)} className={`${dashboardBtnInvoiceDelete} shrink-0`}>
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteRow;
